/**
 * El paso Confirmar del asistente (29/10/2026), puro: lo que dice la tarjeta «Vas a mandar» (`components/VasAMandar.tsx`)
 * y la confirmación reforzada de los envíos grandes.
 *
 * - La audiencia en palabras sale de lo que la campaña tiene GUARDADO (`CampanaDetalle.audiencia` y su `seguimientoDe`),
 *   no de lo que se tocó en el paso Audiencia sin guardar: es lo que va a salir. Las listas que valen para todas las
 *   campañas salen del chequeo (`exclusiones.listas` con `siempre`): las que de verdad se aplicaron.
 * - Con los nombres de las opciones de «A quién» (`NOMBRE_AUDIENCIA`, 29/10/2026) y el detalle a continuación: «Todos tus
 *   clientes · todos los segmentos», «Una lista guardada: Clientes de octubre». Antes decía los nombres internos («Del
 *   Público de Activa», «Un público de tu base»). Fuera del asistente (la lista, el detalle) sigue el nombre guardado.
 * - Confirmación reforzada: con más de `UMBRAL_PERSONAS_REFUERZO` personas o un costo máximo de más de
 *   `UMBRAL_COSTO_REFUERZO_USD` (lo que se descuenta del saldo como mucho), hay que escribir la cantidad de personas antes
 *   de mandar. Sin ventana: un campo en la tarjeta. Lo escrito vale sólo para ESA cantidad: si el chequeo cambia, se
 *   vuelve a escribir.
 */

import type { CampanaDetalle, Chequeo, GrupoAudiencia } from "../types";
import type { Pestana } from "./asistente";
import { describirFiltroCompras } from "./compras";
import { describirExclusiones, excluirDeAudiencia, textoSiempre, unirConY } from "./exclusiones";
import { CATEGORIA } from "./textos";
import { fechaHoraEnZona, paisDeZona } from "./zonas";
import { num, plural } from "../../campana-nueva/utils/format";

/** Lo elegido en «Cuándo» (null = todavía nada), la fecha programada (ISO) y la zona de la campaña. */
export type CuandoDelEnvio = { modo: "ahora" | "programar" | null; iso: string | null; zona: string };

/**
 * Cuándo sale, en una línea: «Ahora, apenas confirmes», «El 03/11/26, 10:00 (hora de Argentina)» o, con «Ahora» y la
 * franja cerrada según el chequeo, «Apenas abra la franja: el 29/10/26, 10:00 (hora de Argentina)». La dicen igual «Vas a
 * mandar» y «Tu campaña». null si falta elegir: cada uno dice qué falta a su manera.
 */
export function cuandoSaleEnPalabras(c: CuandoDelEnvio, chequeo: Pick<Chequeo, "enFranjaAhora" | "proximaApertura"> | null | undefined): string | null {
  const hora = (iso: string) => `${fechaHoraEnZona(iso, c.zona)} (hora de ${paisDeZona(c.zona)})`;
  if (c.modo === "programar") return c.iso ? `El ${hora(c.iso)}` : null;
  if (c.modo !== "ahora") return null;
  return chequeo?.enFranjaAhora === false && chequeo.proximaApertura ? `Apenas abra la franja: el ${hora(chequeo.proximaApertura)}` : "Ahora, apenas confirmes";
}

/** Con MÁS de esta cantidad de personas, antes de mandar hay que escribir la cantidad. */
export const UMBRAL_PERSONAS_REFUERZO = 1_000;
/** Con un costo máximo (lo que se descuenta del saldo como mucho) de MÁS de estos dólares, también. */
export const UMBRAL_COSTO_REFUERZO_USD = 50;

/** «1 persona» / «1.234 personas»: la misma palabra en la tarjeta, el resumen y el botón. */
export function textoPersonas(n: number): string {
  return `${num(n)} ${plural(n, "persona", "personas")}`;
}

/** ¿Este envío pide escribir la cantidad antes de mandar? */
export function pideRefuerzo(c: Pick<Chequeo, "envio" | "costo"> | null | undefined): boolean {
  if (!c) return false;
  return (Number(c.envio) || 0) > UMBRAL_PERSONAS_REFUERZO || (Number(c.costo?.clienteMaximoUSD) || 0) > UMBRAL_COSTO_REFUERZO_USD;
}

/** Lo escrito es la cantidad: sólo cuentan los dígitos («1.234», «1234» y «1 234» valen para 1.234). */
export function refuerzoCumplido(escrito: string, envio: number): boolean {
  const d = String(escrito || "").replace(/\D/g, "");
  return d !== "" && Number(d) === envio;
}

/** «Escribí 1.234 para confirmar». */
export function textoRefuerzo(envio: number): string {
  return `Escribí ${num(envio)} para confirmar`;
}

/** «Marketing» / «Utilidad»; una que no se conoce, como venga. */
export function textoCategoria(categoria: string | null | undefined): string {
  const c = String(categoria || "");
  return (CATEGORIA as Record<string, string>)[c] || c;
}

/** Las cuatro opciones de «A quién», con los nombres del comerciante: los mismos en el paso, en «Tu campaña» y en «Vas a mandar». */
export const NOMBRE_AUDIENCIA: Record<Pestana, string> = {
  publico: "Todos tus clientes", base: "Una lista guardada", excel: "Un archivo", activa: "Los grupos de una segmentación",
};

/**
 * El Público de Activa en palabras: «Todos tus clientes · En riesgo» o, sólo con los de un archivo, «Un archivo: campeones
 * · todos los segmentos». `archivo`: null sin archivo; "" si no se sabe cómo se llama. `detalle`: el segmento y la actividad.
 */
export function origenDelPublico(o: { archivo: string | null; detalle: string[] }): string {
  const resto = o.detalle.filter(Boolean);
  const cola = resto.length ? ` · ${resto.join(" · ")}` : "";
  if (o.archivo === null) return `${NOMBRE_AUDIENCIA.publico}${cola}`;
  return `${NOMBRE_AUDIENCIA.excel}${o.archivo ? `: ${o.archivo}` : ""}${cola}`;
}

/**
 * Una segmentación en palabras: «Los grupos de una segmentación: HATSU · octubre (todos)», con los elegidos por su nombre
 * («Dormidos» y «En riesgo») o, si no se conocen todos, cuántos. `grupos`: los ids elegidos ([] = todos).
 */
export function origenDeLaSegmentacion(o: { analisis: string; grupos: string[]; nombres: string[] }): string {
  const de = `${NOMBRE_AUDIENCIA.activa}${o.analisis ? `: ${o.analisis}` : ""}`;
  if (!o.grupos.length) return `${de} (todos)`;
  if (o.nombres.length === o.grupos.length) return `${de} (${unirConY(o.nombres)})`;
  return `${de} (${num(o.grupos.length)} ${plural(o.grupos.length, "grupo", "grupos")})`;
}

/** El nombre que guarda el backend para el Público (`nombreDeAudiencia`): «Público de Activa · del archivo «x» · <segmento> · <actividad>». */
function partesDelPublico(nombre: string): { archivo: string | null; detalle: string[] } {
  const resto = String(nombre || "").split(" · ").slice(1).filter(Boolean);
  const abre = "del archivo «";
  if (!resto[0]?.startsWith(abre)) return { archivo: null, detalle: resto };
  // el nombre del archivo puede traer « · »: termina en la parte que cierra las comillas
  const cierra = resto.findIndex((p) => p.endsWith("»"));
  if (cierra < 0) return { archivo: "", detalle: resto.slice(1) };
  return { archivo: resto.slice(0, cierra + 1).join(" · ").slice(abre.length, -1), detalle: resto.slice(cierra + 1) };
}

/** La audiencia en palabras, renglón por renglón (lo que no aplica, vacío). */
export interface AudienciaEnPalabras {
  /** de dónde sale: «Todos tus clientes · En riesgo», «Una lista guardada: Clientes de prueba»… */
  origen: string;
  /** el filtro «Por compras», una condición por renglón (sólo Público y Base) */
  compras: string[];
  /** las exclusiones elegidas para esta campaña: «Sin los que recibieron «Promo octubre»», «Sin la lista «VIP»» */
  elegidas: string[];
  /** las listas que valen para todas las campañas: «Siempre se saca a quien está en «Empleados».» */
  siempre: string;
}

/**
 * La audiencia guardada de la campaña, en palabras. `grupos`: los del análisis de Activa (para nombrar los elegidos;
 * sin ellos, cuántos). `chequeo`: el de Confirmar, del que salen las listas que valen siempre.
 */
export function audienciaEnPalabras(
  c: Pick<CampanaDetalle, "audiencia" | "seguimientoDe"> | null | undefined,
  o: { grupos?: GrupoAudiencia[] | null; chequeo?: Pick<Chequeo, "exclusiones"> | null } = {},
): AudienciaEnPalabras {
  const a = c?.audiencia ?? null;
  const siempre = textoSiempre((o.chequeo?.exclusiones?.listas || []).filter((l) => l.siempre).map((l) => l.nombre));
  const elegidas = (() => {
    if (!a?.excluir) return [];
    const nombres = new Map<string, string>([
      ...a.excluir.campanas.map((x) => [x.id, x.nombre] as [string, string]),
      ...a.excluir.listas.map((x) => [x.id, x.nombre] as [string, string]),
    ]);
    return describirExclusiones(excluirDeAudiencia(a.excluir), nombres);
  })();
  const s = c?.seguimientoDe;
  if (s) return { origen: `Seguimiento de «${s.nombre || "la campaña original"}»${s.texto ? `: ${s.texto}` : ""}`, compras: [], elegidas, siempre };
  if (!a) return { origen: "Todavía no elegiste a quiénes", compras: [], elegidas: [], siempre };
  const compras = a.tipo === "activa" ? [] : describirFiltroCompras(a.filtroCompras);
  if (a.tipo === "publico") return { origen: origenDelPublico(partesDelPublico(a.nombre)), compras, elegidas, siempre };
  if (a.tipo === "base") return { origen: `${NOMBRE_AUDIENCIA.base}: ${a.nombre || "sin nombre"}`, compras, elegidas, siempre };
  const grupos = a.grupos || [];
  const nombres = grupos.map((id) => o.grupos?.find((g) => g.id === id)?.nombre).filter((x): x is string => !!x);
  return { origen: origenDeLaSegmentacion({ analisis: a.nombre, grupos, nombres }), compras, elegidas, siempre };
}
